import { cloneDeep, set } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import {
  HotelIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
} from '@apps/main/modules/recipe/ingredients';

import type { NeighborhoodPolygonsOnlyHotel } from '@libs/trip-planner-interfaces';

import type {
  HotelFilterPillData,
  HotelFilterUpdate,
  HotelFiltersForm,
} from '../model/hotelFilters';
import { DEFAULT_HOTEL_FILTERS, HotelFilter } from '../model/hotelFilters';

import {
  getActiveFilters,
  getFullStarsArray,
  getHotelFilterFromIngredient,
} from './hotellFiltersStoreLogic';

export const useHotelFiltersStore = defineStore('HotelFilters', () => {
  // State
  const form = ref<HotelFiltersForm>(cloneDeep(DEFAULT_HOTEL_FILTERS));
  // Actions
  function setFiltersValuesFromIngredient(
    stayIngredient: HotelIngredient | OwnStayIngredient
  ): void {
    const filters = getHotelFilterFromIngredient(
      isOwnStayIngredient(stayIngredient)
        ? HotelIngredient.defaultFilters
        : stayIngredient.filters
    );
    form.value = filters;
  }

  function updateFormValue(filterUpdate: HotelFilterUpdate): void {
    switch (filterUpdate.type) {
      case HotelFilter.RatingStars: {
        const newValues = getFullStarsArray(filterUpdate.value);
        set(form.value, filterUpdate.type, newValues);
        break;
      }
      case HotelFilter.HotelName: {
        set(form.value, filterUpdate.type, filterUpdate.value.trim());
        break;
      }
      case HotelFilter.NeighborhoodPolygons: {
        if (form.value.neighborhoodPolygons.includes(filterUpdate.value)) {
          set(
            form.value,
            filterUpdate.type,
            form.value.neighborhoodPolygons.filter(
              (element) => element !== filterUpdate.value
            )
          );
        } else {
          set(form.value, filterUpdate.type, [
            ...form.value.neighborhoodPolygons,
            filterUpdate.value,
          ]);
        }
        break;
      }
      default:
        set(form.value, filterUpdate.type, filterUpdate.value);
        break;
    }
  }

  function resetAllFiltersToDefault(): void {
    form.value = cloneDeep(DEFAULT_HOTEL_FILTERS);
  }

  function clearMealFilters(): void {
    form.value.meals = DEFAULT_HOTEL_FILTERS.meals;
  }

  function clearAmenitiesFilters(): void {
    form.value.amenities = DEFAULT_HOTEL_FILTERS.amenities;
  }

  function clearAccommodationTypeFilters(): void {
    form.value.typeOfAccommodation = DEFAULT_HOTEL_FILTERS.typeOfAccommodation;
  }

  function clearNeighborhoodPolygonsFilters(): void {
    form.value.neighborhoodPolygons =
      DEFAULT_HOTEL_FILTERS.neighborhoodPolygons;
  }

  // Getters
  const activeFilters = computed(
    () =>
      (
        ingredient: HotelIngredient | OwnStayIngredient | null | undefined
      ): HotelFilterPillData[] => {
        let neighborhoodPolygons: NeighborhoodPolygonsOnlyHotel[] = [];
        if (ingredient && isHotelIngredient(ingredient)) {
          neighborhoodPolygons = ingredient.neighborhoodPolygons;
        }
        return getActiveFilters(form.value, neighborhoodPolygons);
      }
  );

  return {
    //State
    form,

    // Actions
    setFiltersValuesFromIngredient,
    updateFormValue,
    resetAllFiltersToDefault,
    clearMealFilters,
    clearAmenitiesFilters,
    clearAccommodationTypeFilters,
    clearNeighborhoodPolygonsFilters,

    // Getters
    activeFilters,
  };
});
